import { useSyncExternalStore } from 'react';

/**
 * Whether payroll should ask the member which employee is them. One question for the whole section,
 * opened from wherever the need is found: a page that sees no owner on the family, or any payroll
 * request the server refused with `OWNER_NOT_FOUND`.
 *
 * Module state rather than React Query or context, because the refusal is seen in the API client's
 * error handler, outside render. Deliberately not persisted: a reload re-reads the family.
 */
let isRequested = false;
let wasAskedUpFront = false;
const listeners = new Set<() => void>();

const publish = (requested: boolean) => {
    if (isRequested !== requested) {
        isRequested = requested;
        listeners.forEach((listener) => listener());
    }
};

export function requestOwnerConfirmation() {
    publish(true);
}

/**
 * Asked at most once per page load, so a member who closes the question can keep browsing. A refused
 * request still asks again, because that write cannot go through until they answer.
 */
export function requestOwnerConfirmationUpFront() {
    if (!wasAskedUpFront) {
        wasAskedUpFront = true;
        publish(true);
    }
}

export function dismissOwnerConfirmation() {
    publish(false);
}

/** As on a fresh page load: nothing asked yet, up front or otherwise. For stories and tests. */
export function forgetOwnerConfirmation() {
    wasAskedUpFront = false;
    publish(false);
}

const subscribe = (listener: () => void) => {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};

const getSnapshot = () => isRequested;

export function useOwnerConfirmationRequested(): boolean {
    return useSyncExternalStore(subscribe, getSnapshot);
}
